import ImageLightbox from '../components/ImageLightbox';
import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ChevronLeft, ChevronRight, Star, Quote, ArrowRight, CheckCircle2, X, ZoomIn } from 'lucide-react';
import { SERVICES } from '../data';
import { useSiteInfo } from '../context/SiteContext';
import Sidebar from '../components/Sidebar';
import NewsGrid from '../components/NewsGrid';
import ContactForm from '../components/ContactForm';
import { publicCollection } from '../lib/publicContent';
import { LocalBusinessSchema } from '../components/SEO/SchemaMarkup';
import { SITE_PHOTOS, servicePhoto } from '../sitePhotos';
import AdBlock from '../components/AdBlock';

const LOCAL_BANNERS = [
  '/assets/optimized/banner-1.webp',
  '/assets/optimized/banner-2.webp',
  '/assets/optimized/banner-3.webp'
];

// About banner - placeholder until admin sets one
const ABOUT_BANNER = '/assets/optimized/home-main.webp';

interface Project {
  id: string;
  imageUrl: string;
  name: string;
  status?: 'ongoing' | 'completed';
}

// Banner item: ảnh desktop + tùy chọn ảnh mobile riêng
interface BannerItem {
  url: string;
  mobileUrl?: string;
  title?: string;
}

interface GalleryItem {
  id: string;
  imageUrl: string;
  caption?: string;
  order?: number;
}

// Section heading component with animated underline
function SectionHeading({ title, id }: { title: string; id?: string }) {
  return (
    <div className="mb-7" id={id}>
      <h2 className="text-2xl md:text-3xl font-bold tracking-tight text-primary inline-block relative pb-3">
        {title}
        <span className="absolute bottom-0 left-0 h-[3px] rounded-full bg-[#c5a03b] w-12" />
      </h2>
      <div className="mt-1 h-[1px] bg-gradient-to-r from-primary/20 to-transparent" />
    </div>
  );
}

export default function Home() {
  const { siteInfo, banners, gallery, services: actualServices } = useSiteInfo();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [projects, setProjects] = useState<Project[]>([]);
  const [projectTab, setProjectTab] = useState<'all' | 'ongoing' | 'completed'>('all');
  const [lightboxImg, setLightboxImg] = useState<string | null>(null);
  const statsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    publicCollection('projects').then(rows=>setProjects(rows.sort((a,b)=>(a.order||0)-(b.order||0)))).catch(console.error);
  }, []);

  const displayBanners: BannerItem[] = banners.length > 0
    ? banners
    : [];

  useEffect(() => {
    if (displayBanners.length <= 1) return;
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % displayBanners.length);
    }, 8000);
    return () => clearInterval(timer);
  }, [displayBanners.length, currentSlide]);

  const nextSlide = () => setCurrentSlide((prev) => (prev + 1) % displayBanners.length);
  const prevSlide = () => setCurrentSlide((prev) => (prev - 1 + displayBanners.length) % displayBanners.length);

  // Filter projects by tab
  const filteredProjects = (() => {
    const base = [...projects];
    // Source photos fill empty spaces without fabricating project addresses.
    while (base.length < 6) {
      const photo = SITE_PHOTOS[base.length];
      base.push({ id: `source-${photo.id}`, imageUrl: photo.url, name: photo.caption });
    }
    if (projectTab === 'ongoing') return base.filter(p => p.status === 'ongoing');
    if (projectTab === 'completed') return base.filter(p => p.status === 'completed');
    return base;
  })();

  // Gallery fallback items
  const displayGallery = gallery.length > 0 ? gallery : SITE_PHOTOS.map(photo => ({
    id: photo.id, imageUrl: photo.url, caption: photo.caption,
  }));

  // Service sections config
  const serviceSections = [
    {
      title: 'Khoan Cọc Nhồi',
      filter: (s: typeof SERVICES[0]) => (s.title.toLowerCase().includes('khoan cọc nhồi') || ['khoan-dan-ep-coc', 'tham-do-dia-chat'].includes(s.id)) && !s.title.toLowerCase().includes('tại'),
      viewMoreLink: '/khoan-coc-nhoi-dan-dung',
    },
    {
      title: 'Khoan Cọc Nhồi Tại Hà Nội',
      filter: (s: typeof SERVICES[0]) => s.title.toLowerCase().includes('khoan cọc nhồi') && s.title.toLowerCase().includes('tại'),
      viewMoreLink: '/khoan-coc-nhoi-tai-ha-noi',
    },
    {
      title: 'Chống Lún Nhà',
      filter: (s: typeof SERVICES[0]) => !s.title.toLowerCase().includes('khoan cọc nhồi'),
      viewMoreLink: '/chong-lun-nha',
    },
  ];

  const hasMobileBanner = banners.some(slide => slide.mobileUrl);

  const siteCanonical = "https://www.khoancocnhoivietnam.vn";

  // JSON-LD: LocalBusiness schema
  const websiteSchema = { "@context": "https://schema.org", "@type": "WebSite", "name": "Nền Móng Việt Nam", "url": siteCanonical };

  const metaDescription = "Khoan khô, khoan dẫn và khoan cọc nhồi tại Hà Nội, miền Bắc. Trao đổi phương án theo hồ sơ và mặt bằng công trình. Hotline: 0968 387 568.";

  return (
    <div className="w-full">
      <Helmet>
        <title>Khoan Cọc Nhồi Hà Nội – Uy Tín, Chuyên Nghiệp | Nền Móng Việt Nam</title>
        <meta name="description" content={metaDescription} />
        <link rel="canonical" href={siteCanonical} />
        <meta property="og:title" content="Khoan Cọc Nhồi Hà Nội – Uy Tín, Chuyên Nghiệp" />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={siteCanonical} />
        <script type="application/ld+json">{JSON.stringify(websiteSchema)}</script>
      </Helmet>

      {/* LocalBusiness Schema via Component */}
      <LocalBusinessSchema
        name={siteInfo.name}
        description={metaDescription}
        url={siteInfo.baseUrl || siteCanonical}
        telephone={siteInfo.phone}
        email={siteInfo.email}
        address={{
          streetAddress: 'Số 9F, ngõ 77/47/28 Xuân La',
          addressLocality: 'Phường Tây Hồ',
          addressRegion: 'Hà Nội',
          addressCountry: 'VN',
        }}
        image={siteInfo.logoUrl || siteInfo.ogImage}
        priceRange="$$"
        openingHours="Mo-Sa 07:30-17:30"
        sameAs={[siteInfo.facebookUrl, siteInfo.zaloUrl || `https://zalo.me/${siteInfo.phone.replace(/\D/g, '')}`].filter((url): url is string => Boolean(url))}
      />

      {/* Hero Slider — mobile aspect-[16/7] ≈ banner ratio (1920×600), no letterbox */}
      {siteInfo.bannerSlots?.['home_slider'] !== false && (
        <div className={`relative w-full ${hasMobileBanner ? 'aspect-[6/5]' : 'aspect-[3/1]'} md:aspect-[1920/600] overflow-hidden group bg-primary-dark`}>
          {displayBanners.map((slide, index) => {
            const activeIndex = currentSlide % displayBanners.length;
            return (
              <div
                key={slide.url || index}
                className={`absolute inset-x-0 top-0 ${hasMobileBanner ? 'bottom-8' : 'bottom-0'} md:bottom-0 transition-opacity duration-[1200ms] ease-in-out motion-reduce:transition-none ${index === activeIndex ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
              >
                {/* <picture> cho phép browser tự chọn ảnh mobile/desktop */}
                <picture className="w-full h-full block">
                  {slide.mobileUrl && (
                    <source 
                      media="(max-width: 767px)" 
                      srcSet={slide.mobileUrl} 
                    />
                  )}
                  <img
                    src={slide.url}
                    alt={slide.title || 'Dịch vụ nền móng — Nền Móng Việt Nam'}
                    className="w-full h-full object-contain md:object-cover object-center"
                    fetchPriority={index === 0 ? "high" : "auto"}
                    loading={index === 0 ? "eager" : "lazy"}
                    decoding="async"
                    onError={(e) => {
                      e.currentTarget.src = `https://dummyimage.com/1920x600/f1f5f9/94a3b8.png&text=Banner+Demo+(1920x600)`;
                    }}
                  />
                </picture>
              </div>
            );
          })}
          {displayBanners.length > 1 && (
            <>
              <button onClick={prevSlide} aria-label="Banner trước" className="absolute left-2 sm:left-3 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/60 text-white p-2.5 rounded-full opacity-100 sm:opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity cursor-pointer z-10">
                <ChevronLeft size={24} />
              </button>
              <button onClick={nextSlide} aria-label="Banner tiếp theo" className="absolute right-2 sm:right-3 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/60 text-white p-2.5 rounded-full opacity-100 sm:opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity cursor-pointer z-10">
                <ChevronRight size={24} />
              </button>
            </>
          )}
          {displayBanners.length > 1 && (
            <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex space-x-2 z-10">
              {displayBanners.map((_, index) => (
                <button key={index} onClick={() => setCurrentSlide(index)} aria-label={`Xem banner ${index + 1}`} aria-pressed={index === currentSlide % displayBanners.length}
                  className={`w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full transition-all cursor-pointer ${index === (currentSlide % displayBanners.length) ? 'bg-[#d4af50] scale-125' : 'bg-white/50 hover:bg-white'}`}
                />
              ))}
            </div>
          )}
        </div>
      )}

      <div className="border-b border-slate-200/70 bg-white">
        <div className="container mx-auto grid grid-cols-2 md:grid-cols-4 gap-4 px-4 py-5">
          {['Khoan khô', 'Khoan dẫn', 'Khoan cọc nhồi', 'Hà Nội & miền Bắc'].map(label => (
            <div key={label} className="flex items-center gap-2 text-sm md:text-base font-semibold text-primary">
              <CheckCircle2 size={19} className="text-[#ac8426] shrink-0" />{label}
            </div>
          ))}
        </div>
      </div>

      {/* Main Content Area */}
      <div className="container mx-auto px-4 py-8 md:py-12 flex flex-col md:flex-row gap-8">
        {/* Left Content */}
        <div className="w-full md:w-3/4 min-w-0">

          {/* ===== ABOUT BANNER ===== */}
          <div className="mb-14 mt-4 flex flex-col md:flex-row gap-8 lg:gap-12 items-stretch bg-white rounded-3xl p-5 md:p-7 border border-slate-200/60 shadow-sm">
            <div className="flex-1 flex flex-col justify-center">
              <h3 className="text-sm font-bold text-gray-800 uppercase tracking-wider mb-2">Về chúng tôi</h3>
              <h1 className="text-3xl md:text-4xl font-black text-primary mb-5 leading-tight">
                <span className="block">Khoan cọc nhồi</span>
                <span className="block">Hà Nội &amp; miền Bắc</span>
              </h1>
              <p className="text-slate-600 text-base leading-7 mb-6">
                {siteInfo.name} cung cấp dịch vụ khoan khô, khoan dẫn và khoan cọc nhồi tại Hà Nội và miền Bắc. Mỗi công trình được trao đổi phương án từ thiết kế, điều kiện địa chất và mặt bằng thực tế. Gửi vị trí và hồ sơ để cùng chuẩn bị bước thi công nền móng.
              </p>
              <div>
                  <Link to="/gioi-thieu" className="inline-flex items-center justify-center rounded-xl bg-primary px-6 py-3 font-semibold text-white hover:bg-primary-dark transition-colors focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-primary">
                    Xem thêm
                  </Link>
              </div>
            </div>
            
            <div className="flex-1 w-full mx-auto max-w-md md:max-w-none px-4 md:px-0 py-4 relative">
              {/* Decorative shapes to match reference */}
              <div className="absolute top-0 right-0 w-24 h-24 border-t-[10px] border-r-[10px] border-primary z-0"></div>
              <div className="absolute bottom-0 left-0 w-32 h-32 bg-gray-100 z-0"></div>
              <div className="absolute bottom-6 left-6 w-32 h-32 border-b border-l border-gray-300 border-dashed z-0"></div>
              
              <div className="relative z-10 w-full aspect-[4/3] rounded-2xl overflow-hidden shadow-lg border-4 border-white bg-white">
                <img
                  src={siteInfo.aboutBanner || SITE_PHOTOS[7].url}
                  alt="Giới thiệu công ty"
                  className="w-full h-full object-cover"
                  loading="lazy"
                  decoding="async"
                  onError={(e) => {
                    e.currentTarget.onerror = null;
                    e.currentTarget.src = SITE_PHOTOS[7].url;
                  }}
                />
              </div>
            </div>
          </div>

          {/* ===== SERVICE SECTIONS ===== */}
          {serviceSections.map((section) => {
            const sectionServices = actualServices.filter(section.filter).slice(0, 6);
            return (
              <section key={section.title} className="mb-12">
                <SectionHeading title={section.title} />
                <p className="mb-5 -mt-3 text-sm text-slate-500">Ảnh tư liệu thi công của đơn vị.</p>
                <div className="mobile-card-rail grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6" tabIndex={0} aria-label={section.title}>
                  {sectionServices.map((service) => (
                    <div key={service.id} className="bg-white rounded-2xl overflow-hidden shadow-sm border border-slate-200/70 hover:shadow-lg hover:-translate-y-1 transition-all duration-300 motion-reduce:transform-none group flex flex-col">
                      {/* Thumbnail */}
                      <Link to={`/${service.slug}`} className={`block overflow-hidden ${service.id.startsWith("khoan-coc-nhoi-tai-") ? "aspect-video" : "aspect-[4/3]"} bg-slate-100 flex-shrink-0`}>
                        <img
                          src={servicePhoto(service.id, sectionServices.indexOf(service), service.image)}
                          alt={service.title}
                          style={{ objectPosition: servicePhoto(service.id, sectionServices.indexOf(service), service.image).includes("a022") ? "40% 40%" : "50% 40%" }}
                          className={`w-full h-full ${service.id.startsWith("khoan-coc-nhoi-tai-") ? "object-contain" : "object-cover group-hover:scale-[1.02]"} transition-transform duration-500 motion-reduce:transform-none`}
                          loading="lazy"
                          decoding="async"
                          onError={(e) => {
                            e.currentTarget.onerror = null; e.currentTarget.src = SITE_PHOTOS[0].url;
                          }}
                        />
                      </Link>
                      {/* Card body */}
                      <div className="p-4 flex flex-col flex-grow">
                        <h3 className="font-bold text-base mb-2 leading-snug text-slate-900">
                          <Link to={`/${service.slug}`} className="hover:text-primary transition-colors" title={service.title}>
                            {service.title}
                          </Link>
                        </h3>
                        {/* Excerpt: 1 dòng mobile, 2 dòng desktop */}
                        {service.description && (
                          <p className="text-gray-600 text-sm line-clamp-1 sm:line-clamp-2 mb-2 flex-grow">
                            {service.description}
                          </p>
                        )}
                        {/* Xem thêm per card */}
                        <Link
                          to={`/${service.slug}`}
                          className="inline-flex items-center gap-1 text-primary hover:text-primary-dark text-xs font-semibold transition-colors mt-auto group/link"
                        >
                          Xem thêm
                          <ArrowRight size={11} className="group-hover/link:translate-x-0.5 transition-transform" />
                        </Link>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            );
          })}

          {/* Quick Contact Form */}
          <section className="mb-12">
            <ContactForm collapsible={true} />
          </section>

          {/* ===== CÔNG TRÌNH ĐÃ THI CÔNG ===== */}
          <section className="mb-12">
            {/* Title + full-width divider */}
            <div className="mb-5">
              <h2 className="text-2xl font-bold text-primary uppercase pb-3">
                Công trình đã thi công
              </h2>
              <div className="h-[2px] bg-gradient-to-r from-primary via-primary/40 to-transparent" />
            </div>

            {/* Projects grid */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {filteredProjects.slice(0, Math.max(6, projects.length)).map((project) => (
                <button
                  type="button"
                  aria-label={`Xem ảnh công trình: ${project.name}`}
                  key={project.id}
                  className="relative group overflow-hidden rounded-2xl bg-slate-100 aspect-[4/3] shadow-sm cursor-zoom-in hover:shadow-md transition-shadow"
                  onClick={() => setLightboxImg(project.imageUrl)}
                >
                  <img
                    src={project.imageUrl}
                    alt={project.name}
                      style={{ objectPosition: "50% 40%" }}
                    className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-500 motion-reduce:transform-none"
                    loading="lazy"
                    decoding="async"
                  />
                  {/* Zoom icon on hover */}
                  <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <ZoomIn size={28} className="text-white drop-shadow-lg" />
                  </div>
                  {/* Name label */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent transition-opacity flex items-end p-3">
                    <span className="text-white font-medium text-sm drop-shadow-md truncate">{project.name}</span>
                  </div>
                </button>
              ))}
            </div>

            {filteredProjects.length === 0 && (
              <p className="text-center text-gray-400 py-8">Chưa có công trình nào trong mục này</p>
            )}
          </section>



          {/* Testimonials */}
          {siteInfo.showTestimonials && (
            <section className="mb-12 bg-[#f8fbff] p-8 rounded-xl border border-blue-50">
              <h2 className="text-2xl font-bold text-primary uppercase mb-8 text-center">
                Khách hàng nói gì về chúng tôi
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {[
                  {
                    name: "Anh Hoàng Phúc",
                    role: "Chủ thầu xây dựng (Cầu Giấy)",
                    text: "Tôi đã hợp tác với Nền Móng Việt Nam hơn 5 năm qua. Đội ngũ thi công cực kỳ chuyên nghiệp, máy móc hiện đại vào được cả ngõ hẻm nhỏ nhất. Đáng giá nhất là chưa bao giờ làm ảnh hưởng đến nhà lân cận.",
                  },
                  {
                    name: "Chị Ngọc Anh",
                    role: "Chủ nhà phố (Hoàn Kiếm)",
                    text: "Nhà tôi nằm trong khu phố cổ ngõ siêu nhỏ, nền đất yếu lắm. Các bên khác vào đều lắc đầu nhưng các anh bên Việt Nam đã đưa máy mini tháo rời vào và thi công cọc D300 rất êm ái, an toàn. Rất cảm ơn công ty!",
                  },
                  {
                    name: "Anh Tuấn Minh",
                    role: "Giám đốc công trình (Tây Hồ)",
                    text: "Tiến độ là vấn đề tôi quan tâm nhất khi xây chung cư mini. Nhờ chọn dịch vụ của công ty mà tôi có thể đóng cọc xong toàn bộ hơn 40 cọc D500 chỉ trong chưa đầy 1 tuần. Giá cả cũng rất cạnh tranh.",
                  },
                  {
                    name: "Bác Hùng",
                    role: "Chủ công trình cải tạo (Đống Đa)",
                    text: "Sau đợt thi công chống lún và nâng tầng, nền móng nhà tôi bây giờ rất vững chắc. Việc thi công được tư vấn kỹ từ đầu, có sự giám sát liên tục nên tôi rất yên tâm. 5 sao cho chất lượng dịch vụ.",
                  }
                ].map((item, idx) => (
                   <div key={idx} className="bg-white p-6 rounded-lg shadow-sm border border-gray-100 flex flex-col relative overflow-hidden group">
                     <Quote size={40} className="absolute text-blue-50 -top-2 -left-2 opacity-50 group-hover:opacity-100 transition-opacity" />
                     <div className="flex text-yellow-400 mb-3 relative z-10">
                       <Star size={16} fill="currentColor" />
                       <Star size={16} fill="currentColor" />
                       <Star size={16} fill="currentColor" />
                       <Star size={16} fill="currentColor" />
                       <Star size={16} fill="currentColor" />
                     </div>
                     <p className="text-gray-600 italic text-sm mb-4 flex-grow relative z-10">"{item.text}"</p>
                     <div className="flex items-center mt-auto relative z-10">
                       <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-bold mr-3 border-2 border-white shadow-sm">
                         {item.name.charAt(item.name.startsWith('Anh') || item.name.startsWith('Chị') || item.name.startsWith('Bác') ? 4 : 0)}
                       </div>
                       <div>
                         <h4 className="font-bold text-sm text-gray-800">{item.name}</h4>
                         <span className="text-xs text-gray-500">{item.role}</span>
                       </div>
                     </div>
                   </div>
                ))}
              </div>
            </section>
          )}

          {/* News Section */}
          <section>
            <SectionHeading title="Tin tức & Sự kiện" />
            <NewsGrid mobileRail />
          </section>

          {/* ===== HÌNH ẢNH THỰC TẾ GALLERY (Tọa lạc chân trang) ===== */}
          <section className="mt-12 mb-12" id="hinh-anh-thuc-te">
            <SectionHeading title="Hình ảnh thực tế" />
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {displayGallery.map((item) => (
                <button
                  type="button"
                  aria-label={`Xem ảnh thực tế: ${item.caption || 'Ảnh tư liệu thi công'}`}
                  key={item.id}
                  className="relative group overflow-hidden rounded-2xl bg-slate-100 aspect-[4/3] cursor-zoom-in shadow-sm hover:shadow-md transition-shadow"
                  onClick={() => setLightboxImg(item.imageUrl)}
                >
                  <img
                    src={item.imageUrl}
                    alt={item.caption || 'Hình ảnh thực tế'}
                    className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-500 motion-reduce:transform-none"
                    loading="lazy"
                    decoding="async"
                    onError={(e) => {
                      e.currentTarget.onerror = null; e.currentTarget.src = SITE_PHOTOS[0].url;
                    }}
                  />
                  {/* Zoom icon on hover */}
                  <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <ZoomIn size={28} className="text-white drop-shadow-lg" />
                  </div>
                  {item.caption && (
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent px-3 py-3 transition-opacity">
                      <p className="text-white text-xs font-medium truncate">{item.caption}</p>
                    </div>
                  )}
                </button>
              ))}
            </div>
          </section>

          <ImageLightbox image={lightboxImg} onClose={() => setLightboxImg(null)} />

        </div>

        {/* Right Sidebar */}
        <div className="w-full md:w-1/4 md:shrink-0 md:flex md:flex-col">
          <Sidebar />
          <div className="hidden md:block mt-auto pt-10">
            <AdBlock position="home_gallery_sidebar" />
          </div>
        </div>
      </div>

      {/* Map Section */}
      <div className="w-full h-[350px] md:h-[450px]">
        <iframe
          src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3723.473663215204!2d105.8054453154025!3d21.053730992301!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3135ab1966555555%3A0x5555555555555555!2zTmfDtSA3NyBYdcOibiBMYSwgVMOieSBI4buTLCBIw6AgTuG7mWksIFZp4buHdCBOYW0!5e0!3m2!1svi!2s!4v1711333333333!5m2!1svi!2s"
          width="100%"
          height="100%"
          style={{ border: 0 }}
          allowFullScreen={true}
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
        ></iframe>
      </div>
    </div>
  );
}
